<template>
  <component :is="componentName" v-if="componentName !== 'math'">
    <template v-if="!isMathInline && !isIframeTag">
      <ContentItem
        v-for="(childNode, index) in node.children"
        :key="index"
        :node="childNode"
        :style="styles"
        :page="page"
      />
    </template>

    <template v-if="isTextNode && !hasHyperLink(content)">{{ content }}</template>

    <template v-if="isTextNode && hasHyperLink(content)">
      <span
        v-for="(value, index) in parseHyperLinkContent(content)"
        :key="index"
      >
        <span
          v-if="value.type === 'Element' && value.tagName === 'a'"
          @click="handleLinkClick(value.attributes.href)"
          class="link"
        >
          {{ getChildrenContent(value.children) }}
        </span>
        <template v-else>{{ value.content }}</template>
      </span>
    </template>

    <span v-if="isMathInline" v-html="getHTML(node.children)"></span>

    <span v-if="isIframeTag" v-html="getHTML(node.children)"></span>

    <ContentVideo
      v-if="isVideoTag"
      :video="getVideo(node.attributes.dataset.videoId)"
    >
    </ContentVideo>

    <content-image
      v-if="isImageTag"
      :src="node.attributes.src"
      :image="getImage(node.attributes.dataset.imageId)"
    >
    </content-image>
  </component>
</template>

<script>
import { mapGetters } from 'vuex';
import { findIndex, head, map, filter, isEmpty, isString } from 'lodash';
import himalayaTranslate from 'himalaya/translate';
import { parse } from 'himalaya';
import he from 'he';
import linkifyHtml from 'linkifyjs/html';
import { find } from 'linkifyjs';
import Mobile from '@/mixins/Mobile';

export default {
  name: 'content-item',

  mixins: [
    Mobile,
  ],

  props: [
    'node',
    'page',
  ],

  computed: {
    ...mapGetters({
      isDevice: 'getIsDevice',
    }),

    componentName() {
      return this.hasChildrenNode
        ? this.node.tagName
        : 'span';
    },

    content() {
      if (!this.node.content) return null;

      return he.decode(this.replacePlaceHolderContent(this.node.content));
    },

    styles() {
      if (this.node.attributes &&
            this.node.attributes.style &&
            (this.node.attributes.style.color ||
             this.node.attributes.style.backgroundColor)) {
        return this.node.attributes.style;
      }

      return null;
    },

    isMathInline() {
      return this.node.attributes &&
          this.node.attributes.id &&
          isString(this.node.attributes.id) &&
          this.node.attributes.id.indexOf('math-inline-') === 0;
    },

    isIframeTag() {
      return this.node.children &&
          this.node.children.length > 0 &&
          this.node.children[0].tagName === 'iframe';
    },

    isImageTag() {
      return this.node.tagName === 'img';
    },

    isVideoTag() {
      return this.node.tagName === 'video';
    },

    isTextNode() {
      return this.node.type === 'Text';
    },

    hasChildrenNode() {
      return this.node.type === 'Element'
          && this.node.tagName !== 'img'
          && this.node.tagName !== 'video';
    },
  },

  methods: {
    getHTML(nodes) {
      return himalayaTranslate.toHTML(nodes);
    },

    replacePlaceHolderContent(content) {
      if (content === '문제 해설을 입력하세요.') {
        const answer = this.getQuizCorrentAnswer(this.page);
        return answer ? `문제의 정답은 ${answer} 입니다.` : answer;
      }

      return content;
    },

    getQuizCorrentAnswer(page) {
      if (isEmpty(page.answers)) return null;
      const correctAnswerNumber = findIndex(page.answers, 'is_correct') + 1;

      if (
        page.page_type.name === 'multiple_choice'
          && correctAnswerNumber > 0
      ) {
        return `${correctAnswerNumber}번`;
      }

      if (page.page_type.name === 'text_entry') {
        return head(map(filter(page.answers, 'is_correct'), 'content'));
      }

      return null;
    },

    handleLinkClick(link) {
      if (this.isDevice) {
        return this.openWebview(link);
      }

      return window.open(link);
    },

    hasHyperLink(content) {
      return !isEmpty(find(content));
    },

    parseHyperLinkContent(content) {
      const result = linkifyHtml(content, {
        defaultProtocol: 'http',
      });

      return parse(result);
    },

    getChildrenContent(children) {
      if (isEmpty(children)) return '';
      return head(children).content || '';
    },

    getVideo(videoId) {
      return this.page.videos.find(video => video.id === videoId);
    },

    getImage(imageId) {
      if (isEmpty(this.page.images)) return {};
      return this.page.images.find(item => item.id === imageId);
    },
  },
};
</script>

<style lang="scss" scoped>
  u {
    text-underline-position: under;
  }

  .link {
    color: #0275d8;
    cursor: pointer;
  }
</style>
